import { ApiService } from "@/services/api/ApiService";

import type { Database } from "@/integrations/supabase/types";
import type { ActivationEmailDeliveryResult } from "./ActivationPackService";
import type { CustomerMemoryConsentStatus } from "@/types/voice/customer-memory";
// DEPRECATED: agent_pages table is obsolete and scheduled for deletion — do not add new usages.
export type AgentPage = Database["public"]["Tables"]["agent_pages"]["Row"];

export class GenieAccessService {
  static async getAgentPageByUrlName(
    urlName: string,
    accessToken?: string,
  ): Promise<{ agent: any; userMemory: string | null } | null> {
    const response = await ApiService.invoke<{
      agent: any;
      userMemory?: string | null;
      memoryConsent?: CustomerMemoryConsentStatus;
    }>(
      {
        resource: "genies",
        action: "url",
        data: { url_name: urlName },
        ...(accessToken && {
          headers: { "x-agent-access-token": accessToken },
        }),
      },
      "public",
    );
    return response?.agent
      ? {
          agent: {
            ...response.agent,
            userMemory: response.userMemory || null,
            memoryConsent: response.memoryConsent || "unset",
          },
          userMemory: response.userMemory || null,
        }
      : null;
  }

  static async requestAccess(urlName: string, email: string): Promise<void> {
    await ApiService.invoke(
      {
        resource: "genies",
        action: "request-access",
        data: { url_name: urlName, email },
      },
      "public",
    );
  }

  static async unlockWithPassword(
    urlName: string,
    password: string,
  ): Promise<{ token: string; expires_at: string }> {
    return ApiService.invoke<{ token: string; expires_at: string }>(
      {
        resource: "genies",
        action: "unlock",
        data: { url_name: urlName, password },
      },
      "public",
    );
  }

  static async acceptInvite(
    agentId: string,
    opts?: { urlName?: string; email?: string },
  ): Promise<{ consumerId: string | null }> {
    const isPublic = !!opts?.email;
    const response = await ApiService.invoke<{ consumerId: string | null }>(
      {
        resource: "genies",
        action: "accept-invite",
        data: {
          agent_id: agentId,
          ...(opts?.urlName && { url_name: opts.urlName }),
          ...(opts?.email && { email: opts.email }),
        },
      },
      isPublic ? "public" : undefined,
    );
    return { consumerId: response?.consumerId ?? null };
  }

  static async getAgentUsers(agentId: string): Promise<any[]> {
    const response = await ApiService.invoke<{ users: any[] }>({
      resource: "genie-access",
      action: "all",
      id: agentId,
    });
    return response?.users ?? [];
  }

  static async updateAgentUser(
    agentId: string,
    userId: string,
    expiresAt: string | null,
  ): Promise<any> {
    const response = await ApiService.invoke<{ agentUser: any }>({
      resource: "genie-access",
      action: "update-user",
      data: { agentId, userId, expires_at: expiresAt },
    });
    return response?.agentUser ?? null;
  }

  static async deleteAgentUser(agentId: string, userId: string): Promise<void> {
    await ApiService.invoke({
      resource: "genie-access",
      action: "delete-user",
      data: { agentId, userId },
    });
  }

  static async getAccessRequests(agentId: string): Promise<any[]> {
    const response = await ApiService.invoke<{ requests: any[] }>({
      resource: "genie-access",
      action: "requests",
      id: agentId,
    });
    return response?.requests ?? [];
  }

  static async resolveAccessRequest(
    requestId: number,
    solution: "approved" | "denied",
    expiresAt?: string | null,
  ): Promise<{
    resolved: boolean;
    solution: "approved" | "denied";
    deliveryState?: "delivered" | "unknown";
    warning?: string;
  }> {
    return await ApiService.invoke({
      resource: "genie-access",
      action: "resolve",
      data: { requestId, solution, expires_at: expiresAt ?? null },
    });
  }

  static async inviteUsers(
    agentId: string,
    emails: string[],
    campaignId: string,
    expiresAt?: string | null,
  ): Promise<ActivationEmailDeliveryResult[]> {
    const response = await ApiService.invoke<{
      invited: ActivationEmailDeliveryResult[];
    }>({
      resource: "genie-access",
      action: "invite",
      data: { agentId, emails, expires_at: expiresAt ?? null, campaignId },
    });
    return response?.invited ?? [];
  }
}
